import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from "react-router-dom";

// Helper functions
import { getSettings, notifyError } from '../../../settings/Helpers';
import api from '../../../settings/AxiosSetup';

// 3rd party
import * as Icon from 'react-bootstrap-icons';
import 'moment/locale/nl';
import moment from 'moment';
import 'moment-timezone';
import toast, { Toaster } from 'react-hot-toast';
import {
    Chart as ChartJS,
    LinearScale,
    CategoryScale,
    BarElement,
    PointElement,
    LineElement,
    Legend,
    Tooltip,
    LineController,
    BarController,
    Title,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';

// Components
import SideNav from '../../../components/sidenav/sidenav';
import BottomNav from '../../../components/bottomnav/bottomnav';
import DashboardHeader from '../../../components/dashboard/header';

// Assets
import svzArtwork from '../../../assets/images/artwork-svz.svg';

// Styles
import './rapportage.css';
import 'react-loading-skeleton/dist/skeleton.css';

const SchadeRapportage = () => {

    ChartJS.register(
        LinearScale,
        CategoryScale,
        BarElement,
        PointElement,
        LineElement,
        Legend,
        Tooltip,
        LineController,
        BarController,
        Title
    );

    const navigate = useNavigate();
    const location = useLocation();
    const loggedIn = localStorage.getItem('ingelogd');
    const medewerker = JSON.parse(localStorage.getItem('medewerker'));

    const [year, setYear] = useState(moment().year());
    const [loadedYear, setLoadedYear] = useState(moment().year());

    const [lineChartData, setLineChartData] = useState({ labels: [], datasets: [] });
    const [barChartDataSoort, setBarChartDataSoort] = useState({ labels: [], datasets: [] });
    const [barChartType, setBarChartType] = useState({ labels: [], datasets: [] });
    const [barChartStandplaats, setBarChartStandplaats] = useState({ labels: [], ids: [], datasets: [] });

    const getRapportage = async () => {
        try {
            const response = await api.get("/schades/rapportage/" + year);
            if (response.data) {
                setLoadedYear(year);
                const {
                    total_schades,
                    total_schades_schuld,
                    total_schades_charter,
                    total_schades_letsel,
                    total_schades_per_type,
                    total_schades_per_soort,
                    total_schades_standplaats
                } = response.data;

                // === Bepaal beschikbare maanden ===
                const availableMonths = [
                    ...new Set([
                        ...Object.keys(total_schades),
                        ...Object.keys(total_schades_schuld),
                        ...Object.keys(total_schades_charter),
                        ...Object.keys(total_schades_letsel),
                    ]),
                ];

                // === LIJNGRAFIEK ===
                setLineChartData({
                    labels: availableMonths,
                    datasets: [
                        {
                            type: 'line',
                            label: 'Schades totaal',
                            data: availableMonths.map(month => total_schades[month] || 0),
                            borderColor: 'rgb(255, 99, 132)',
                            backgroundColor: 'rgba(255, 99, 132, 0.5)',
                            fill: true,
                        },
                        {
                            type: 'line',
                            label: 'Schuldschades',
                            data: availableMonths.map(month => total_schades_schuld[month] || 0),
                            borderColor: 'rgb(54, 162, 235)',
                            backgroundColor: 'rgba(54, 162, 235, 0.5)',
                            fill: true,
                        },
                        {
                            type: 'line',
                            label: 'Charterschades',
                            data: availableMonths.map(month => total_schades_charter[month] || 0),
                            borderColor: 'rgb(75, 192, 192)',
                            backgroundColor: 'rgba(75, 192, 192, 0.5)',
                            fill: true,
                        },
                        {
                            type: 'line',
                            label: 'Letselschades',
                            data: availableMonths.map(month => total_schades_letsel[month] || 0),
                            borderColor: 'rgb(153, 102, 255)',
                            backgroundColor: 'rgba(153, 102, 255, 0.5)',
                            fill: true,
                        },
                    ],
                });

                // === BAR CHART PER TYPE ===
                const labelsSoort = Object.keys(total_schades_per_type[availableMonths[0]] || {});
                setBarChartDataSoort({
                    labels: availableMonths,
                    datasets: labelsSoort.map(type => {
                        const randomColor = getRandomColor();
                        return {
                            type: 'bar',
                            label: type,
                            data: availableMonths.map(month => total_schades_per_type[month]?.[type] || 0),
                            borderColor: randomColor,
                            backgroundColor: randomColor,
                        };
                    }),
                });

                // === BAR CHART PER SOORT ===
                const labelsBar = Object.keys(total_schades_per_soort[availableMonths[0]] || {});
                setBarChartType({
                    labels: availableMonths,
                    datasets: labelsBar.map(category => {
                        const randomColor = getRandomColor();
                        return {
                            type: 'bar',
                            label: category,
                            data: availableMonths.map(month => total_schades_per_soort[month]?.[category] || 0),
                            borderColor: randomColor,
                            backgroundColor: randomColor,
                        };
                    }),
                });

                // === BAR CHART PER STANDPLAATS (TOTAAL, GESPLITST OP GEARCHIVEERD) ===
                const totalPerStandplaats = {};
                for (const month in total_schades_standplaats) {
                    for (const id in total_schades_standplaats[month]) {
                        const { naam, actief, gearchiveerd } = total_schades_standplaats[month][id];
                        if (!totalPerStandplaats[id]) {
                            totalPerStandplaats[id] = { naam, actief: 0, gearchiveerd: 0 };
                        }
                        totalPerStandplaats[id].actief += actief;
                        totalPerStandplaats[id].gearchiveerd += gearchiveerd;
                    }
                }

                const sorted = Object.entries(totalPerStandplaats).sort((a, b) =>
                    a[1].naam.localeCompare(b[1].naam, 'nl')
                );

                setBarChartStandplaats({
                    labels: sorted.map(([_, v]) => v.naam),
                    ids: sorted.map(([id]) => id),
                    datasets: [
                        {
                            label: "Actief",
                            data: sorted.map(([_, v]) => v.actief),
                            backgroundColor: "rgba(255, 159, 64, 0.7)",
                            borderColor: "rgba(255, 159, 64, 1)",
                            borderWidth: 1,
                        },
                        {
                            label: "Gearchiveerd",
                            data: sorted.map(([_, v]) => v.gearchiveerd),
                            backgroundColor: "rgba(54, 162, 235, 0.7)",
                            borderColor: "rgba(54, 162, 235, 1)",
                            borderWidth: 1,
                        },
                    ],
                });
            }
        } catch (error) {
            if (!error.response) return navigate("/offline");
        }
    };

    const submitFilters = (event) => {
        event.preventDefault();
        if (year.length !== 4) {
            return notifyError("Vul een geldig jaar in");
        }
        getRapportage();
    };

    const getRandomColor = () => {
        const r = Math.floor(Math.random() * 256);
        const g = Math.floor(Math.random() * 256);
        const b = Math.floor(Math.random() * 256);
        return `rgba(${r}, ${g}, ${b}, 0.7)`;
    };

    useEffect(() => {
        const settings = getSettings();
        settings.then((result) => {
            if (result.onderhoudsmodus) navigate('/onderhoud');
        });
        if (loggedIn !== 'true') navigate('/login');
        if (
            medewerker?.gebruiker?.role?.type !== 'schadebeheer' &&
            medewerker?.gebruiker?.role?.type !== 'super_admin' &&
            medewerker?.gebruiker?.role?.type !== 'controleur'
        ) {
            navigate('/');
        }
        getRapportage();
    }, []);

    return (
        <div id="main" className="dashboard">
            {medewerker && <SideNav page="/rapportage-schades" />}
            <div className="dashboard-content">
                <DashboardHeader title={'Rapportage schades ' + loadedYear} />

                <div className="rapportage-filters">
                    <form onSubmit={submitFilters}>
                        <input
                            type="number"
                            placeholder="Jaar"
                            value={year}
                            onChange={(e) => setYear(e.target.value)}
                            required
                        />
                        <button type="submit" className="button">Filter</button>
                    </form>
                </div>

                <section className="chart-section">
                    <h3>Schades per maand</h3>
                    {lineChartData.datasets.length > 0 ? (
                        <Chart type="bar" data={lineChartData} />
                    ) : <p>Laden...</p>}
                </section>

                <section className="chart-section">
                    <h3>Schades per type</h3>
                    {barChartDataSoort.datasets.length > 0 ? (
                        <Chart type="bar" data={barChartDataSoort} />
                    ) : <p>Laden...</p>}
                </section>

                <section className="chart-section">
                    <h3>Schades per soort</h3>
                    {barChartType.datasets.length > 0 ? (
                        <Chart type="bar" data={barChartType} />
                    ) : <p>Laden...</p>}
                </section>

                <section className="chart-section">
                    <h3>Totaal schades per standplaats</h3>
                    {barChartStandplaats.datasets.length > 0 ? (
                        <Chart
                            type="bar"
                            data={barChartStandplaats}
                            options={{
                                responsive: true,
                                plugins: { legend: { display: true } },
                                scales: {
                                    x: { stacked: true },
                                    y: { stacked: true, beginAtZero: true },
                                },
                                onClick: (event, elements) => {
                                    if (elements.length > 0) {
                                        const index = elements[0].index;
                                        const datasetIndex = elements[0].datasetIndex;
                                        const id = barChartStandplaats.ids[index];

                                        if (datasetIndex === 0) {
                                            navigate(`/schades?year=${loadedYear}&standplaats=${id}`);
                                        } else if (datasetIndex === 1) {
                                            navigate(`/gearchiveerd?year=${loadedYear}&standplaats=${id}`);
                                        }
                                    }
                                },
                                onHover: (event, elements) => {
                                    event.native.target.style.cursor =
                                        elements.length > 0 ? 'pointer' : 'default';
                                },
                            }}
                        />
                    ) : <p>Laden...</p>}
                </section>

                <Toaster />
            </div>

            {medewerker && <BottomNav page="/rapportage-schades" />}
            <img src={svzArtwork} className="artwork" alt="artwork" />
        </div>
    );
};

export default SchadeRapportage;